'use client';

import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from '@google-recaptcha/react';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { ClientConfig } from 'services/clientConfig';
import { validateAction } from './validateAction';

const { enableRecaptcha, recaptchaSiteKey } =
  ClientConfig.getInstance().getConfig();

const useReCaptchaControl = enableRecaptcha
  ? useGoogleReCaptcha
  : () => ({ executeV3: undefined, googleReCaptcha: undefined });

interface ReCaptchaContextData {
  /**
   * @param action needs to be in camelCase
   */
  isActionValid(action: string): Promise<boolean>;
  isLoaded: boolean;
}

const ReCaptchaContext = createContext({} as ReCaptchaContextData);

const ReCaptchaContextProviderBase = (props: PropsWithChildren) => {
  const { children } = props;

  const { executeV3, googleReCaptcha } = useReCaptchaControl();

  const isLoaded = useMemo(() => Boolean(googleReCaptcha), [googleReCaptcha]);

  const isActionValid = async (action: string) => {
    try {
      if (!enableRecaptcha) return true;

      const token = (await executeV3?.(action)) || '';

      return validateAction(token, origin);
    } catch (error) {
      return false;
    }
  };

  const value = useMemo(() => {
    return { isActionValid, isLoaded };
  }, [isActionValid, isLoaded]);

  return (
    <ReCaptchaContext.Provider value={value}>
      {children}
    </ReCaptchaContext.Provider>
  );
};

export const ReCaptchaContextProvider = (props: PropsWithChildren) => {
  if (!enableRecaptcha) return <ReCaptchaContextProviderBase {...props} />;

  return (
    <GoogleReCaptchaProvider siteKey={recaptchaSiteKey} type="v3">
      <ReCaptchaContextProviderBase {...props} />
    </GoogleReCaptchaProvider>
  );
};

export const useReCaptcha = () => useContext(ReCaptchaContext);
